import React, { useEffect, useState, useCallback } from "react";
import { Container, Row, Col, Card, CardBody, Table, Button, Input, Badge, Spinner } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { toast } from "react-toastify";
import { getEmailLogsApi, getSmtpStatusApi, deleteEmailLogApi } from "../../apiServices/emailLogServices";
import EmailPreviewModal from "./EmailPreviewModal";
import SendTestMailModal from "./SendTestMailModal";
import DeleteModal from "../../components/Common/DeleteModal";

const STATUS_FILTERS = [
  { label: "All Messages", value: "All", icon: "mdi mdi-email-multiple-outline" },
  { label: "Sent", value: "Sent", icon: "mdi mdi-check-decagram-outline" },
  { label: "Failed", value: "Failed", icon: "mdi mdi-alert-circle-outline" },
];

const MailerDashboard = () => {
  document.title = "Mailer Infrastructure | RS-CRM";

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [smtpStatus, setSmtpStatus] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const [testMailModalOpen, setTestMailModalOpen] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchSmtpStatus = async () => {
    try {
      const res = await getSmtpStatusApi();
      const statusData = res?.data || res || {};
      setSmtpStatus(statusData);
    } catch (err) {
      console.error("Failed to fetch SMTP status:", err);
    }
  };

  const fetchEmailLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 10,
        search,
        status: selectedStatus,
      };
      const res = await getEmailLogsApi(params);
      const logData = res?.data || res || {};
      setLogs(logData.items || []);
      setTotal(logData.total || 0);
      setTotalPages(logData.totalPages || 1);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to fetch email logs");
    } finally {
      setLoading(false);
    }
  }, [page, search, selectedStatus]);

  useEffect(() => {
    fetchSmtpStatus();
    fetchEmailLogs();
  }, [fetchEmailLogs]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleStatusFilter = (st) => {
    setSelectedStatus(st);
    setPage(1);
  };

  const handlePreviewClick = (log) => {
    setSelectedLog(log);
    setPreviewModalOpen(true);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleteLoading(true);
      await deleteEmailLogApi(deleteId);
      toast.success("Email log entry deleted successfully");
      setDeleteModalOpen(false);
      setDeleteId(null);
      fetchEmailLogs();
      fetchSmtpStatus();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete email log");
    } finally {
      setDeleteLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Sent":
        return "success";
      case "Failed":
        return "danger";
      case "Pending":
        return "warning";
      default:
        return "primary";
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="SuperAdmin" breadcrumbItem="Mailer & Sent Messages" />

          {/* Top SMTP Status Banner */}
          <Row className="mb-3">
            <Col md={12}>
              <Card
                className="border-0 shadow-sm text-white overflow-hidden"
                style={{
                  background: "linear-gradient(135deg, #1e1e2d 0%, #2b2d42 50%, #4B49AC 100%)",
                  borderRadius: "14px",
                }}
              >
                <CardBody className="p-4">
                  <Row className="align-items-center">
                    <Col md={7}>
                      <div className="d-flex align-items-center">
                        <div
                          className="bg-white shadow-sm d-flex align-items-center justify-content-center me-3"
                          style={{
                            width: "50px",
                            height: "50px",
                            minWidth: "50px",
                            minHeight: "50px",
                            maxWidth: "50px",
                            maxHeight: "50px",
                            borderRadius: "50%",
                            flexShrink: 0,
                            aspectRatio: "1 / 1",
                          }}
                        >
                          <i className="mdi mdi-email-outline font-size-24 text-primary"></i>
                        </div>
                        <div>
                          <h4 className="text-white fw-bold mb-1 d-flex align-items-center gap-2">
                            <span>SMTP Mailer Infrastructure</span>
                            <Badge color="success" className="px-2 py-1 font-size-11 rounded-pill">
                              <i className="mdi mdi-check-circle-outline me-1"></i> Active
                            </Badge>
                          </h4>
                          <p className="mb-0 text-white-50 font-size-13">
                            Track dispatched messages, verify SMTP server connectivity, and inspect rich HTML previews like SendGrid & Mailtrap.
                          </p>
                        </div>
                      </div>
                    </Col>
                    <Col md={5} className="text-md-end mt-3 mt-md-0">
                      <div className="d-flex gap-2 justify-content-md-end">
                        <Button
                          color="light"
                          className="px-3 shadow-sm border-0 font-weight-semibold"
                          onClick={() => {
                            fetchSmtpStatus();
                            fetchEmailLogs();
                          }}
                          disabled={loading}
                        >
                          <i className={`mdi mdi-refresh me-1 ${loading ? "mdi-spin text-primary" : "text-primary"}`}></i> Refresh
                        </Button>
                        {/* <Button
                          color="success"
                          className="px-3 text-white fw-semibold shadow-sm"
                          onClick={() => setTestMailModalOpen(true)}
                        >
                          <i className="mdi mdi-send me-1"></i> Send Test Mail
                        </Button> */}
                      </div>
                    </Col>
                  </Row>

                  {/* SMTP Status Quick Metrics */}
                  {smtpStatus && (
                    <div className="row g-3 mt-3 pt-3 border-top border-white border-opacity-15 text-white">
                      <div className="col-6 col-md-3">
                        <small className="text-white-50 d-block font-size-11 text-uppercase fw-semibold">
                          <i className="mdi mdi-server-network me-1 text-info"></i> SMTP Host
                        </small>
                        <span className="fw-bold font-size-14">{smtpStatus.smtpHost || "smtp.gmail.com"}</span>
                      </div>
                      <div className="col-6 col-md-3">
                        <small className="text-white-50 d-block font-size-11 text-uppercase fw-semibold">
                          <i className="mdi mdi-shield-lock-outline me-1 text-warning"></i> Port / Security
                        </small>
                        <span className="fw-bold font-size-14">
                          {smtpStatus.smtpPort || 465} {smtpStatus.isSecure ? "(SSL)" : "(TLS)"}
                        </span>
                      </div>
                      <div className="col-6 col-md-3">
                        <small className="text-white-50 d-block font-size-11 text-uppercase fw-semibold">
                          <i className="mdi mdi-email-multiple-outline me-1 text-light"></i> Total Dispatched
                        </small>
                        <span className="fw-bold font-size-14">{smtpStatus.stats?.totalCount || total || 0} Messages</span>
                      </div>
                      <div className="col-6 col-md-3">
                        <small className="text-white-50 d-block font-size-11 text-uppercase fw-semibold">
                          <i className="mdi mdi-chart-timeline-variant me-1 text-success"></i> Delivery Success Rate
                        </small>
                        <span className="fw-bold font-size-14 text-warning">
                          {smtpStatus.stats?.successRate || 100}%
                        </span>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Status Filter Tabs & Search */}
          <Card className="mb-3 border shadow-sm">
            <CardBody className="p-2">
              <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <div className="d-flex flex-wrap gap-2">
                  {STATUS_FILTERS.map((st) => (
                    <Button
                      key={st.value}
                      size="sm"
                      color={selectedStatus === st.value ? "primary" : "soft-secondary"}
                      onClick={() => handleStatusFilter(st.value)}
                      className="px-3"
                    >
                      <i className={`${st.icon} me-1`}></i>
                      {st.value === "All" ? `All Messages (${total})` : st.label}
                    </Button>
                  ))}
                </div>

                <div style={{ minWidth: "280px" }} className="w-100 w-md-auto">
                  <div className="position-relative">
                    <Input
                      type="text"
                      placeholder="Search by recipient, subject, category..."
                      value={search}
                      onChange={handleSearchChange}
                      className="form-control-sm pe-4"
                    />
                    <i className="mdi mdi-magnify position-absolute top-50 end-0 translate-middle-y me-2 text-muted"></i>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Sent Messages History Table (Desktop View) */}
          <Card className="d-none d-md-block border shadow-sm">
            <CardBody className="p-0">
              <div className="table-responsive">
                <Table hover responsive className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: "220px" }}>
                        <i className="mdi mdi-account-arrow-right-outline me-1 text-primary"></i> Recipient
                      </th>
                      <th style={{ minWidth: "240px" }}>
                        <i className="mdi mdi-text-subject me-1 text-info"></i> Subject
                      </th>
                      <th style={{ minWidth: "120px" }}>
                        <i className="mdi mdi-tag-outline me-1 text-secondary"></i> Category
                      </th>
                      <th style={{ minWidth: "170px" }}>
                        <i className="mdi mdi-clock-outline me-1 text-warning"></i> Sent Date & Time
                      </th>
                      <th style={{ minWidth: "110px" }}>
                        <i className="mdi mdi-shield-check-outline me-1 text-success"></i> Status
                      </th>
                      <th style={{ width: "160px", textAlign: "right" }}>
                        <i className="mdi mdi-cog-outline me-1"></i> Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="text-center py-5">
                          <Spinner color="primary" />
                          <div className="text-muted mt-2 font-size-13">Loading email log history...</div>
                        </td>
                      </tr>
                    ) : logs.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-5 text-muted">
                          <i className="mdi mdi-email-off-outline font-size-36 d-block mb-2 text-secondary"></i>
                          No email log messages found.
                        </td>
                      </tr>
                    ) : (
                      logs.map((log) => (
                        <tr
                          key={log._id}
                          style={{ cursor: "pointer" }}
                          onClick={() => handlePreviewClick(log)}
                          title="Click row to view full email preview"
                        >
                          <td>
                            <div className="fw-semibold text-primary font-size-14">
                              <i className="mdi mdi-email-outline me-1 text-success font-size-16"></i>
                              {log.recipient}
                            </div>
                            <small className="text-muted font-size-11">
                              Via {log.smtpHost || "smtp.gmail.com"}
                            </small>
                          </td>
                          <td>
                            <div className="fw-bold text-dark font-size-13 text-truncate" style={{ maxWidth: "260px" }}>
                              {log.subject}
                            </div>
                            {log.sentBy && (
                              <small className="text-muted font-size-11">
                                By {log.sentBy.name || "System"}
                              </small>
                            )}
                          </td>
                          <td>
                            <Badge color="soft-info" className="px-2 py-1 font-size-11">
                              <i className="mdi mdi-tag-text-outline me-1"></i>
                              {log.category || "System"}
                            </Badge>
                          </td>
                          <td className="font-size-12 text-muted">
                            <i className="mdi mdi-clock-outline me-1"></i>
                            {new Date(log.sentAt || log.createdAt).toLocaleString("en-IN")}
                          </td>
                          <td>
                            <Badge color={getStatusBadge(log.status)} className="px-3 py-1 font-size-12 rounded-pill">
                              {log.status === "Sent" ? (
                                <><i className="mdi mdi-check-circle-outline me-1"></i> Sent</>
                              ) : (
                                <><i className="mdi mdi-alert-circle-outline me-1"></i> Failed</>
                              )}
                            </Badge>
                          </td>
                          <td className="text-end" onClick={(e) => e.stopPropagation()}>
                            <div className="d-flex justify-content-end gap-2">
                              <Button
                                color="soft-primary"
                                className="btn-sm px-2 py-1"
                                title="Preview Message"
                                onClick={() => handlePreviewClick(log)}
                              >
                                <i className="mdi mdi-eye font-size-18 me-1"></i> Preview
                              </Button>
                              <Button
                                color="soft-danger"
                                className="btn-sm px-2 py-1"
                                title="Delete Log"
                                onClick={() => handleDeleteClick(log._id)}
                              >
                                <i className="mdi mdi-trash-can font-size-18"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-3 border-top bg-light">
                  <div className="text-muted font-size-13">
                    Showing page {page} of {totalPages} ({total} messages)
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      color="secondary"
                      disabled={page <= 1 || loading}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      <i className="mdi mdi-chevron-left me-1"></i> Previous
                    </Button>
                    <Button
                      size="sm"
                      color="secondary"
                      disabled={page >= totalPages || loading}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Next <i className="mdi mdi-chevron-right ms-1"></i>
                    </Button>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Mobile Card List View (Visible < 768px) */}
          <div className="d-block d-md-none mb-3">
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <div className="text-muted mt-2">Loading email logs...</div>
              </div>
            ) : logs.length === 0 ? (
              <Card className="text-center py-5 text-muted border shadow-sm">
                <CardBody>
                  <i className="mdi mdi-email-off-outline font-size-36 d-block mb-2 text-secondary"></i>
                  No email log messages found.
                </CardBody>
              </Card>
            ) : (
              logs.map((log) => (
                <Card
                  key={log._id}
                  className="mb-3 border shadow-sm cursor-pointer"
                  style={{ borderRadius: "12px" }}
                  onClick={() => handlePreviewClick(log)}
                >
                  <CardBody className="p-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <div className="fw-bold text-primary font-size-14">
                          <i className="mdi mdi-email-outline me-1"></i> {log.recipient}
                        </div>
                        <Badge color="soft-info" className="mt-1 font-size-10">
                          {log.category || "System"}
                        </Badge>
                      </div>
                      <Badge color={getStatusBadge(log.status)} className="px-2 py-1 font-size-11 rounded-pill">
                        {log.status}
                      </Badge>
                    </div>

                    <div className="fw-semibold text-dark font-size-13 mb-2">
                      {log.subject}
                    </div>

                    <div className="d-flex justify-content-between align-items-center border-top pt-2 mt-2">
                      <small className="text-muted font-size-11">
                        <i className="mdi mdi-clock-outline me-1"></i>
                        {new Date(log.sentAt || log.createdAt).toLocaleString("en-IN")}
                      </small>
                      <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          color="soft-primary"
                          className="btn-sm px-2 py-1"
                          onClick={() => handlePreviewClick(log)}
                        >
                          <i className="mdi mdi-eye font-size-18 me-1"></i> Preview
                        </Button>
                        <Button
                          color="soft-danger"
                          className="btn-sm px-2 py-1"
                          onClick={() => handleDeleteClick(log._id)}
                        >
                          <i className="mdi mdi-trash-can font-size-18"></i>
                        </Button>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              ))
            )}
          </div>
        </Container>
      </div>

      {/* Modals */}
      <EmailPreviewModal
        isOpen={previewModalOpen}
        toggle={() => setPreviewModalOpen(!previewModalOpen)}
        emailLog={selectedLog}
      />

      <SendTestMailModal
        isOpen={testMailModalOpen}
        toggle={() => setTestMailModalOpen(!testMailModalOpen)}
        onSuccess={() => {
          fetchEmailLogs();
          fetchSmtpStatus();
        }}
      />

      <DeleteModal
        isOpen={deleteModalOpen}
        toggle={() => setDeleteModalOpen(!deleteModalOpen)}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
        title="Delete Email Log Entry?"
        text="Are you sure you want to delete this email log record? This action cannot be undone."
      />
    </React.Fragment>
  );
};

export default MailerDashboard;
